import { baseUrlEndpoint, baseUrlPattern, resolveBaseUrl } from "../baseUrl";
import {
  MODELS_HELPER_TEXT,
  MODELS_PREFIX,
  openAiModelsFetch,
  withAllowedModelsHint,
} from "../models";
import { bearerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";
import { cohereUsage } from "./usage";

// Cohere’s OpenAI-compatible surface, so the model allowlist and usage metering
// work the same way here as for every other OpenAI-compatible provider.
const DEFAULT_COHERE_BASE_URL = "https://api.cohere.com/compatibility/v1";

const cohereModelsFetch = openAiModelsFetch(
  "Fetch models from Cohere",
  ({ baseUrl }) => baseUrlEndpoint(baseUrl, DEFAULT_COHERE_BASE_URL, "/models")
);

export const cohere = defineConnector({
  identifier: "cohere",
  name: "Cohere",
  tagLine: "AI models and embeddings",
  logo,
  protocol: "http",
  tags: ["ai", "engineering"],
  defaultUrlPattern: ({ baseUrl }) =>
    baseUrlPattern(baseUrl, DEFAULT_COHERE_BASE_URL),
  auth: (app) => bearerAuth(app.secret.apiKey),
  steps: [
    {
      title: "Create an API key",
      items: [
        {
          kind: "markdown",
          content:
            "Go to [API keys](https://dashboard.cohere.com/api-keys) in the Cohere dashboard and create a new key.",
        },
      ],
    },
    {
      title: "Paste your API key",
      items: [
        {
          kind: "input",
          label: "API key",
          required: true,
          target: { kind: "secrets", field: "apiKey" },
        },
        {
          kind: "url",
          label: "Base URL (optional)",
          placeholder: DEFAULT_COHERE_BASE_URL,
          defaultValue: DEFAULT_COHERE_BASE_URL,
          target: { kind: "configuration", field: "baseUrl" },
          helperText:
            "Leave blank for Cohere’s OpenAI-compatible API endpoint.",
        },
      ],
    },
    {
      title: "Choose available models (optional)",
      items: [
        {
          kind: "list",
          label: "Available models",
          prefix: MODELS_PREFIX,
          columns: [{ field: "name", placeholder: "command-a-03-2025" }],
          addLabel: "Add model",
          fetch: cohereModelsFetch,
          helperText: MODELS_HELPER_TEXT,
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl }) =>
      baseUrlEndpoint(baseUrl, DEFAULT_COHERE_BASE_URL, "/models"),
  },
  publicEnvVars: ({ baseUrl }) => ({
    COHERE_BASE_URL: resolveBaseUrl(baseUrl, DEFAULT_COHERE_BASE_URL),
  }),
  usageHint: (configuration) =>
    withAllowedModelsHint(
      "Use process.env.COHERE_BASE_URL as the base URL for Cohere’s OpenAI-compatible API, with any OpenAI client. Pass a placeholder API key; the proxy injects the real key from the connected app.",
      configuration
    ),
  usage: cohereUsage(),
});
